import { AssessmentSelectDto, AssessmentSubtype, AssessmentType, TechnologyTimeline } from '@@types/api.types';
import {
  AssessmentRecencyInput,
  isLatestAssessmentForUseDomain as isLatestAssessmentForUseDomainShared,
} from '@azymmetric/shared/utils/assessment-recency.util';
import { formatDate, formatDateUtc, formatMonthYear, formatMonthYearUtc } from './date.util';

export const formatAssessmentDate = (assessment: AssessmentSelectDto) => {
  const { assessmentDate, createdAt } = assessment;

  return assessmentDate ? formatDateUtc(assessmentDate) : formatDate(createdAt);
};

export const formatTimelineDate = (timeline: TechnologyTimeline): string => {
  // Use startDate if available, otherwise fall back to deprecated fields
  if (timeline.startDate) {
    return formatDateUtc(timeline.startDate);
  }

  // Backwards compatibility for deprecated startMonth/startYear fields (uses 1st day of month)
  const { startMonth, startYear } = timeline;
  if (!startMonth || !startYear) {
    return 'Unknown';
  }
  const dateString = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
  return formatDateUtc(dateString);
};

export const formatAssessmentMonthYear = (assessment: AssessmentSelectDto) => {
  const { assessmentDate, createdAt } = assessment;

  return assessmentDate ? formatMonthYearUtc(assessmentDate) : formatMonthYear(createdAt);
};

/**
 * Resolves the phases array from optional min/max phase bounds or preserves an existing phases array.
 * - If the data contains minPhase/maxPhase properties (form data), creates a new array from them (can be empty if user cleared).
 * - Otherwise, preserves the existing phases array (non-form data).
 */
export const resolvePhases = (args: {
  data: Record<string, unknown>;
  existing?: number[];
  maxPhase?: number | null;
  minPhase?: number | null;
}): number[] | undefined => {
  const { data, existing, minPhase, maxPhase } = args;

  const hasMinPhase = minPhase !== undefined && minPhase !== null;
  const hasMaxPhase = maxPhase !== undefined && maxPhase !== null;

  // Check if minPhase/maxPhase properties exist in the data object (even if undefined)
  // This indicates form data where user can clear phases
  const hasMinPhaseProperty = 'minPhase' in data;
  const hasMaxPhaseProperty = 'maxPhase' in data;
  const isFormData = hasMinPhaseProperty || hasMaxPhaseProperty;

  if (isFormData) {
    // Form data: create phases array from minPhase/maxPhase (can be empty if user cleared)
    const phases: number[] = [];
    if (hasMinPhase) phases[0] = minPhase as number;
    if (hasMaxPhase) phases[1] = maxPhase as number;
    return phases;
  }

  // Non-form data: preserve existing phases array
  return existing;
};

/**
 * Determines the TRL title based on assessment type and subtype.
 * Returns: "Military TRL" or "TRL"
 */
export const getTrlTitle = (params: {
  subtype?: AssessmentSubtype;
  type: AssessmentType;
}): string => {
  const { subtype, type } = params;

  const isMilitaryTrl = type === AssessmentType.TRL && subtype === AssessmentSubtype.MILITARY;

  return isMilitaryTrl ? 'Military TRL' : 'TRL';
};

/**
 * Checks if an assessment is a Military TRL assessment.
 */
export const isMilitaryTrlAssessment = (params: { subtype?: AssessmentSubtype; type: AssessmentType }): boolean => {
  const { subtype, type } = params;
  return type === AssessmentType.TRL && subtype === AssessmentSubtype.MILITARY;
};

type TrlAssessmentRecencyInput = Pick<AssessmentSelectDto, 'assessmentDate' | 'createdAt' | 'id'> & {
  domain: { id: string };
  use: { id: string };
};

const toAssessmentRecencyInput = (assessment: TrlAssessmentRecencyInput): AssessmentRecencyInput => ({
  id: assessment.id,
  createdAt: assessment.createdAt,
  //
  assessmentDate: assessment.assessmentDate ?? null,
  domainId: assessment.domain.id,
  useId: assessment.use.id,
});

/**
 * Whether the assessment is the most recent one of its use/domain pair —
 * only that one may generate/regenerate TRL analyses. Delegates
 * to the shared comparator the API guard uses, so the UI gate and the 422
 * always agree.
 * See docs/iterations/AZ-384/AZ-384.DECISIONS.md (D11).
 */
export const isLatestAssessmentForUseDomain = (params: {
  assessment: TrlAssessmentRecencyInput;
  assessments: TrlAssessmentRecencyInput[];
}): boolean => {
  const { assessment, assessments } = params;

  return isLatestAssessmentForUseDomainShared({
    assessment: toAssessmentRecencyInput(assessment),
    assessments: assessments.map(toAssessmentRecencyInput),
  });
};
